import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ArrowDown, Ticket, Handshake } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { IMG, VARNA_2027, TICKET_2027 } from "@/lib/platform";
import { goToTicketCheckout } from "./NationalNav";

const CITY_ROW = [
  { name: "София", status: "Очаквайте скоро", next: false },
  { name: "Пловдив", status: "Очаквайте скоро", next: false },
  { name: "Варна", status: VARNA_2027.dateShort, next: true },
];

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.2, 0.8, 0.2, 1], delay: 0.15 + i * 0.1 },
  }),
};

export default function NationalHero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);
  const [loaded, setLoaded] = useState(false);
  const [activeCity, setActiveCity] = useState("Варна");
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 60);
    return () => clearTimeout(t);
  }, []);

  return (
    <section
      ref={ref}
      data-testid="national-hero"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0B1F3D] text-[#F5EBD9] flex flex-col"
    >
      {/* Cinematic background — replaceable media component */}
      <motion.div
        style={{ y: bgY, backgroundImage: `url(${IMG.heroBg})` }}
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        data-testid="hero-media"
      />
      <div className="absolute inset-0 bg-[#0B1F3D]/60" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B1F3D]/40 via-[#0B1F3D]/55 to-[#0B1F3D]" />
      <div className="absolute inset-0 sunset-glow pointer-events-none" />

      {/* Subtle architectural lines */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            "repeating-linear-gradient(90deg, #F5EBD9 0 1px, transparent 1px 120px)",
        }}
      />

      <div className="relative z-10 flex-1 flex flex-col justify-center max-w-[1440px] w-full mx-auto px-6 sm:px-10 lg:px-16 pt-28 pb-16">
        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="flex items-center gap-4 mb-6 sm:mb-8"
        >
          <span className="h-px w-12 bg-[#F5892E]" />
          <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.34em] text-[#F5EBD9]/80">
            Национални имотни събития
          </span>
        </motion.div>

        {/* Massive headline */}
        <div className="mask-line">
          <motion.h1
            variants={line}
            initial="hidden"
            animate={loaded ? "show" : "hidden"}
            custom={0}
            className="font-display uppercase leading-[0.9] tracking-tight text-[15vw] sm:text-[12vw] lg:text-[9vw] xl:text-[8.5rem]"
          >
            Играта на
          </motion.h1>
        </div>
        <div className="mask-line">
          <motion.h1
            variants={line}
            initial="hidden"
            animate={loaded ? "show" : "hidden"}
            custom={1}
            className="font-display uppercase leading-[0.9] tracking-tight text-[15vw] sm:text-[12vw] lg:text-[9vw] xl:text-[8.5rem] text-[#F5892E]"
            style={{ textShadow: "0 10px 80px rgba(245,137,46,0.35)" }}
          >
            Имотите
          </motion.h1>
        </div>

        {/* Animated secondary headline */}
        <div className="mask-line mt-5">
          <motion.div
            variants={line}
            initial="hidden"
            animate={loaded ? "show" : "hidden"}
            custom={2}
            className="font-serif italic text-xl sm:text-2xl lg:text-3xl text-[#F5EBD9]/85"
            data-testid="hero-secondary"
          >
            Три града. Една игра.
          </motion.div>
        </div>

        {/* Interactive city row */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.7 }}
          data-testid="hero-cities"
          className="mt-10 sm:mt-12 flex flex-col sm:flex-row sm:items-end gap-5 sm:gap-10"
        >
          {CITY_ROW.map((c) => (
            <div
              key={c.name}
              onMouseEnter={() => setActiveCity(c.name)}
              onClick={() => setActiveCity(c.name)}
              data-testid={`hero-city-${c.name.toLowerCase()}`}
              className={`group cursor-pointer transition-all duration-500 ${
                activeCity === c.name ? "opacity-100" : "opacity-45 hover:opacity-80"
              }`}
            >
              <div
                className={`font-display uppercase tracking-tight leading-none transition-all duration-500 ${
                  c.next
                    ? "text-5xl sm:text-6xl lg:text-7xl text-[#F5EBD9]"
                    : "text-4xl sm:text-5xl lg:text-6xl text-[#F5EBD9]"
                }`}
              >
                {c.name}
              </div>
              <div
                className={`mt-2 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.26em] transition-all duration-500 ${
                  activeCity === c.name
                    ? c.next
                      ? "text-[#F5892E]"
                      : "text-[#F5EBD9]/80"
                    : "text-[#F5EBD9]/40"
                }`}
              >
                {c.status}
                {c.next && (
                  <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-[#F5892E] animate-pulse align-middle" />
                )}
              </div>
              <div
                className={`mt-2 h-0.5 transition-all duration-500 ${
                  activeCity === c.name
                    ? c.next
                      ? "w-full bg-[#F5892E]"
                      : "w-full bg-[#F5EBD9]/60"
                    : "w-0 bg-transparent"
                }`}
              />
            </div>
          ))}
        </motion.div>

        {/* Ticket promo + CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={loaded ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-12 sm:mt-14 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10"
        >
          <div data-testid="hero-ticket-info">
            <div className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#F5EBD9]/60">
              Варна | {VARNA_2027.date}
            </div>
            <div className="mt-1.5 font-display uppercase text-2xl sm:text-3xl text-[#F5EBD9]">
              Early Bird — <span className="text-[#F5892E]">{TICKET_2027.price} €</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={goToTicketCheckout}
              data-testid="hero-cta-ticket"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#F5892E] text-[#0B1F3D] font-bold text-sm sm:text-base uppercase tracking-widest hover:bg-[#F5EBD9] transition-colors shadow-[0_15px_50px_-10px_rgba(245,137,46,0.6)]"
            >
              <Ticket className="w-5 h-5" strokeWidth={2.4} />
              Вземи билет за Варна — 34 €
            </button>
            <button
              onClick={() => { navigate("/partners"); window.scrollTo({ top: 0 }); }}
              data-testid="hero-cta-partner"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-[#F5EBD9]/30 text-[#F5EBD9] font-medium text-sm sm:text-base uppercase tracking-widest hover:bg-[#F5EBD9] hover:text-[#0B1F3D] transition-all"
            >
              <Handshake className="w-5 h-5" strokeWidth={2.2} />
              Стани партньор
            </button>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={loaded ? { opacity: 1 } : {}}
        transition={{ delay: 1.6 }}
        className="relative z-10 pb-8 flex justify-center"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center gap-2 text-[#F5EBD9]/50"
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.34em]">Скролни</span>
          <ArrowDown className="w-4 h-4" strokeWidth={2} />
        </motion.div>
      </motion.div>
    </section>
  );
}
